import {
  AppError,
  requireData,
  toAppError,
  type SupabaseError,
} from "../../lib/appError";
import { config } from "../../lib/config";
import type { Database, Json } from "../../lib/database.types";
import { normalizeHostname, subdomainFromHostname } from "../../lib/hostname";
import { supabase } from "../../lib/supabaseClient";
import { domainRepository } from "../domains/domainRepository";

type Site = Database["public"]["Tables"]["sites"]["Row"];
type SiteVersion = Database["public"]["Tables"]["site_versions"]["Row"];

export type ResolvedPublishedSite = {
  site: Site;
  publishedVersionId: string;
};

const toResolved = (site: Site | null): ResolvedPublishedSite | null =>
  site?.published_version_id
    ? { site, publishedVersionId: site.published_version_id }
    : null;

const getPublishedSite = async (
  column: "id" | "subdomain",
  value: string,
): Promise<Site | null> => {
  const { data, error } = await supabase
    .from("sites")
    .select("*")
    .eq(column, value)
    .is("deleted_at", null)
    .not("published_version_id", "is", null)
    .maybeSingle();
  if (error) throw toAppError(error, "Could not load the published website.");
  return data;
};

export const siteRepository = {
  async list(): Promise<Site[]> {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    const user = requireData(
      authData.user,
      authError,
      "You must be signed in to load websites.",
    );
    const { data, error } = await supabase
      .from("sites")
      .select("*")
      .eq("owner_id", user.id)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });
    return requireData(data, error, "Could not load websites.");
  },

  async get(siteId: string): Promise<Site> {
    const { data, error } = await supabase
      .from("sites")
      .select("*")
      .eq("id", siteId)
      .single();
    return requireData(data, error, "Could not load the website.");
  },

  async getVersion(versionId: string): Promise<SiteVersion> {
    const { data, error } = await supabase
      .from("site_versions")
      .select("*")
      .eq("id", versionId)
      .single();
    return requireData(data, error, "Could not load the website version.");
  },

  async create(
    name: string,
    description?: string | null,
    subdomain?: string | null,
  ): Promise<Site> {
    const { data, error } = await supabase.rpc("create_site", {
      site_name: name,
      site_description: description ?? undefined,
      site_subdomain: subdomain ?? undefined,
    });
    return requireData(data, error, "Could not create the website.");
  },

  async createFromTemplate(
    template: string,
    name: string,
    description?: string | null,
  ): Promise<Site> {
    const { data, error } = await supabase.rpc("create_onboarding_site", {
      site_template: template,
      site_name: name,
      site_description: description ?? undefined,
    });
    return requireData(data, error, "Could not create your starter website.");
  },

  async createBlank(
    template: string,
    name: string,
    description?: string | null,
  ): Promise<Site> {
    // create_blank_site arrives with 20261015110843_blank_onboarding_site.sql
    // and isn't in the generated types until that is applied and the types
    // are regenerated. Call it with these arguments and drop the cast then.
    const rpc = supabase.rpc.bind(supabase) as unknown as (
      fn: "create_blank_site",
      args: { site_template: string; site_name: string; site_description?: string },
    ) => PromiseLike<{ data: Site | null; error: SupabaseError | null }>;
    const { data, error } = await rpc("create_blank_site", {
      site_template: template,
      site_name: name,
      site_description: description ?? undefined,
    });
    return requireData(data, error, "Could not create your website.");
  },

  async updateSubdomain(
    siteId: string,
    subdomain: string | null,
  ): Promise<Site> {
    const { data, error } = await supabase
      .from("sites")
      .update({ subdomain })
      .eq("id", siteId)
      .select()
      .single();
    return requireData(data, error, "Could not save the site address.");
  },

  async updateDraft(
    versionId: string,
    values: { name?: string; description?: string | null; settings?: Json },
  ) {
    const { data, error } = await supabase
      .from("site_versions")
      .update(values)
      .eq("id", versionId)
      .select()
      .single();
    return requireData(data, error, "Could not update the website draft.");
  },

  async resolvePublishedSiteByHostname(
    hostname: string,
  ): Promise<ResolvedPublishedSite | null> {
    const normalized = normalizeHostname(hostname);
    if (!normalized) return null;

    const domain = await domainRepository.findActiveByHostname(normalized);
    if (domain) {
      const resolved = toResolved(await getPublishedSite("id", domain.site_id));
      if (resolved) return resolved;
    }

    const subdomain = subdomainFromHostname(
      normalized,
      config.hazumuRootDomain,
    );
    if (!subdomain) return null;
    return toResolved(await getPublishedSite("subdomain", subdomain));
  },

  async publish(siteId: string): Promise<string> {
    const { data, error } = await supabase.rpc("publish_site", {
      target_site_id: siteId,
    });
    if (error?.message?.toLowerCase().includes("subscription")) {
      throw new AppError(error.message, "SUBSCRIPTION_REQUIRED", error);
    }
    return requireData(data, error, "Could not publish the website.");
  },
};
